import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import FormCategoryModal from './FormCategoryModal';

const FormCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Available icons for categories
  const availableIcons = [
    '📋', '📝', '📄', '📊', '📈', '📉', '📋', '📁', '📂', '📌',
    '🔖', '🏷️', '📎', '📏', '📐', '✏️', '🖊️', '🖋️', '✒️', '🖌️',
    '🎨', '🔧', '⚙️', '🔨', '🛠️', '📦', '📮', '📧', '📨', '📩',
    '💼', '👔', '👨‍💼', '👩‍💼', '🏢', '🏭', '🏪', '🏫', '🏥', '🏨',
    '🏦', '🏛️', '⛪', '🕌', '🕍', '🕋', '⛩️', '🏯', '🏰', '🏗️'
  ];

  // Available colors for categories
  const availableColors = [
    '#3B82F6', '#1D4ED8', '#2563EB', '#1E40AF', '#1E3A8A',
    '#10B981', '#059669', '#047857', '#065F46', '#064E3B',
    '#F59E0B', '#D97706', '#B45309', '#92400E', '#78350F',
    '#EF4444', '#DC2626', '#B91C1C', '#991B1B', '#7F1D1D',
    '#8B5CF6', '#7C3AED', '#6D28D9', '#5B21B6', '#4C1D95',
    '#EC4899', '#DB2777', '#BE185D', '#9D174D', '#831843',
    '#06B6D4', '#0891B2', '#0E7490', '#155E75', '#164E63',
    '#84CC16', '#65A30D', '#4D7C0F', '#3F6212', '#365314'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/form-categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching form categories:', error);
      toast.error('Failed to fetch form categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    try {
      const response = await api.delete(`/form-categories/${category._id}`);
      if (response.data.success) {
        toast.success('Form category deleted successfully');
        fetchCategories();
        setShowDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Error deleting form category:', error);
      const message = error.response?.data?.message || 'Failed to delete form category';
      toast.error(message);
    }
  };

  const handleSave = async (categoryData) => {
    try {
      if (editingCategory) {
        // Update existing category
        const response = await api.put(`/form-categories/${editingCategory._id}`, categoryData);
        if (response.data.success) {
          toast.success('Form category updated successfully');
          setShowModal(false);
          fetchCategories();
        }
      } else {
        // Create new category
        const response = await api.post('/form-categories', categoryData);
        if (response.data.success) {
          toast.success('Form category created successfully');
          setShowModal(false);
          fetchCategories();
        }
      }
    } catch (error) {
      console.error('Error saving form category:', error);
      const message = error.response?.data?.message || 'Failed to save form category';
      toast.error(message);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      const response = await api.put(`/form-categories/${category._id}`, {
        isActive: !category.isActive
      });
      if (response.data.success) {
        toast.success(`Form category ${category.isActive ? 'deactivated' : 'activated'} successfully`);
        fetchCategories();
      }
    } catch (error) {
      console.error('Error toggling form category status:', error);
      toast.error('Failed to update form category status');
    }
  };

  const handleSetDefault = async (category) => {
    try {
      const response = await api.put(`/form-categories/${category._id}`, {
        isDefault: true
      });
      if (response.data.success) {
        toast.success('Default form category updated successfully');
        fetchCategories();
      }
    } catch (error) {
      console.error('Error setting default form category:', error);
      toast.error('Failed to set default form category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Form Categories</h2>
          <p className="text-gray-600 mt-1">
            Manage form categories to organize your forms effectively
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div
            key={category._id}
            className={`bg-white rounded-lg border-2 p-6 transition-all duration-200 hover:shadow-md ${
              category.isDefault ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            } ${!category.isActive ? 'opacity-60' : ''}`}
          >
            {/* Category Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div 
                  className="text-2xl"
                  style={{ color: category.color }}
                >
                  {category.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {category.name}
                  </h3>
                  {category.isDefault && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Default
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleEdit(category)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Edit category"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(category)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete category"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Category Description */}
            {category.description && (
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {category.description}
              </p>
            )}

            {/* Category Stats */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
              <span>Sort Order: {category.sortOrder}</span>
              <span>Forms: {category.formCount || 0}</span>
            </div>

            {/* Category Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleToggleActive(category)}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                    category.isActive
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {category.isActive ? (
                    <>
                      <CheckIcon className="h-3 w-3 mr-1" />
                      Active
                    </>
                  ) : (
                    <>
                      <XMarkIcon className="h-3 w-3 mr-1" />
                      Inactive
                    </>
                  )}
                </button>
                {!category.isDefault && (
                  <button
                    onClick={() => handleSetDefault(category)}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                  >
                    Set Default
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No form categories yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first form category to start organizing your forms
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create First Category
          </button>
        </div>
      )}

      {/* Form Category Modal */}
      {showModal && (
        <FormCategoryModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          category={editingCategory}
          onSave={handleSave}
          availableIcons={availableIcons}
          availableColors={availableColors}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Form Category
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{showDeleteConfirm.name}"? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormCategories; 